import { useToast } from "@chakra-ui/react";
import { useIntl } from "react-intl";

import { HeaderActionSection } from "@/components/header-action-section";
import { modelSchemaIsFromCatalogue } from "@/utils/model-schema-is-from-catalogue";

import { useModelSchematisationMetadataSubmit } from "../hooks/use-model-schematisation-metadata-submit";
import { useModelSchematisationPermission } from "../hooks/use-model-schematisation-permission";
import { useSchematisationDetailsContext } from "../schematisation-details-context";

import { ModelSchematisationMetadataTable } from "./model-schematisation-metadata-table";

/**
 * Shared metadata section for both project and catalogue schematisation details.
 * Reads context to determine mode and displays appropriate fields and actions.
 */
export function ModelSchematisationMetadata() {
  const { mode, schematisation, revision } = useSchematisationDetailsContext();

  const intl = useIntl();
  const toast = useToast();
  const { canEditModelSchema } = useModelSchematisationPermission(schematisation.id);

  const schematisationDescription = schematisation.meta?.description ?? "";
  const commitMessage = revision.commitMessage ?? "";
  const { submit, isMutating, tags, setTags } = useModelSchematisationMetadataSubmit();

  const isFromCatalogue = mode === "project" && modelSchemaIsFromCatalogue(schematisation);

  return (
    <HeaderActionSection
      title={intl.formatMessage({ id: "section.metadata", defaultMessage: "Metadata" })}
      action={{
        type: "edit",
        isMutating,
        canEdit: canEditModelSchema,
        initialValues: {
          description: schematisationDescription,
          commitMessage,
        },
        onCancel: () => {
          setTags(schematisation.tags ?? []);
        },
        onEditStart: () => {
          if (isFromCatalogue) {
            toast({
              status: "warning",
              title: intl.formatMessage({
                id: "warning",
                defaultMessage: "Warning",
              }),
              description: intl.formatMessage({
                id: "file.schematisation.metadata_edit_warning",
                defaultMessage:
                  "We are currently working on the way model schematisations are stored. This model schematisation is a copy of the one in the catalogue so editing the metadata will likely also apply those changes to the original model schematisation in the catalogue.",
              }),
              duration: 20000,
            });
          }
        },
        onSubmit: submit,
      }}
    >
      {(editing) => (
        <ModelSchematisationMetadataTable editing={editing} tags={tags} setTags={setTags} />
      )}
    </HeaderActionSection>
  );
}
